// The Cash position chart has two readings of the past:
//
//   'single-anchor'      today's view: one month-end bank balance anchors the
//                        running balance, every month is a model.
//   'month-end-actuals'  past months plot freee's real month-end bank balance
//                        (or the audited one), and the projection starts from
//                        the latest of them (GTM-54).
//
// The second is the only reading today: Tyler approved it, so its switch is
// retired (CASH_MODE_LOCKED below). It drives the chart, the Scenario planner
// and the live-balance marker together so no two panels answer different
// questions.

export type CashMode = 'single-anchor' | 'month-end-actuals'

// Frontend rollback (kill switch): set this to false to hide the switch and
// force 'single-anchor' for everyone, whatever they stored. It wins over
// CASH_MODE_LOCKED, so the GTM-54 rollback's step 1 works while locked.
export const MONTH_END_PREVIEW_AVAILABLE = true

// Tyler approved month-end actuals (Slack, after the 2026-10-28 preview), so
// the switch is retired: while this is set, the switch is hidden and every
// viewer gets this reading, whatever they stored. The switch and the
// single-anchor chart are kept, not deleted. To bring the switch back, set
// this to null.
export const CASH_MODE_LOCKED: CashMode | null = 'month-end-actuals'

// Whether the card offers the switch at all.
export const CASH_MODE_SWITCH_SHOWN = CASH_MODE_LOCKED === null && MONTH_END_PREVIEW_AVAILABLE

// The reading the page draws: the kill switch first, then the lock, then the
// viewer's stored choice.
export function resolveCashMode(
  stored: CashMode,
  locked: CashMode | null,
  available: boolean,
): CashMode {
  if (!available) return 'single-anchor'
  return locked ?? stored
}

export function currentCashMode(stored: CashMode): CashMode {
  return resolveCashMode(stored, CASH_MODE_LOCKED, MONTH_END_PREVIEW_AVAILABLE)
}

// '.v2' since the switch turned ON by default: choices saved while it was OFF
// by default (under 'zk-finance.cash.monthEndPreview') are deliberately
// ignored, so every viewer starts ON once.
export const CASH_MODE_STORAGE_KEY = 'zk-finance.cash.monthEndPreview.v2'

// Reading the global can itself throw (blocked site data) and is absent under
// SSR / the preview renderer.
export function defaultCashStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function readCashMode(storage: Storage | null): CashMode {
  try {
    return storage?.getItem(CASH_MODE_STORAGE_KEY) === 'single-anchor'
      ? 'single-anchor'
      : 'month-end-actuals'
  } catch {
    return 'month-end-actuals'
  }
}

export function writeCashMode(storage: Storage | null, mode: CashMode): void {
  try {
    storage?.setItem(CASH_MODE_STORAGE_KEY, mode)
  } catch {
    // Not remembering is fine; the choice still applies for this visit.
  }
}
